import { cn } from '@/lib/utils';
import { Inbox } from 'lucide-react';

export default function DataTable({
  columns,
  rows,
  loading,
  emptyMessage = 'No data',
  onRowClick,
  keyFn = (r) => r.rowKey || r.id,
  // --- Multi-select (opt-in). Pass selectable=true plus selectedKeys + onSelectionChange. ---
  selectable = false,
  selectedKeys,
  onSelectionChange,
  isRowSelectable, // optional (row) => boolean — rows that return false cannot be ticked
}) {
  if (loading) {
    return (
      <div className="flex items-center justify-center rounded-xl border border-border bg-card p-16 shadow-sm">
        <div className="flex flex-col items-center gap-3 text-muted-foreground">
          <div className="h-6 w-6 animate-spin rounded-full border-[2.5px] border-border border-t-primary" />
          <span className="text-sm">Loading...</span>
        </div>
      </div>
    );
  }

  if (!rows || rows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border bg-card/50 p-16 text-center">
        <Inbox className="mb-3 h-10 w-10 text-muted-foreground/40" />
        <p className="text-sm text-muted-foreground">{emptyMessage}</p>
      </div>
    );
  }

  const selected = selectedKeys instanceof Set ? selectedKeys : new Set(selectedKeys || []);
  const selectableRows = selectable
    ? rows.filter((r) => (isRowSelectable ? isRowSelectable(r) : true))
    : [];
  const allSelected =
    selectable && selectableRows.length > 0 && selectableRows.every((r) => selected.has(keyFn(r)));
  const someSelected =
    selectable && selectableRows.some((r) => selected.has(keyFn(r))) && !allSelected;

  function toggleAll() {
    if (!onSelectionChange) return;
    if (allSelected) {
      onSelectionChange(new Set());
    } else {
      onSelectionChange(new Set(selectableRows.map((r) => keyFn(r))));
    }
  }

  function toggleOne(row) {
    if (!onSelectionChange) return;
    const key = keyFn(row);
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onSelectionChange(next);
  }

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/50">
              {selectable && (
                <th className="w-10 px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    aria-label="Select all"
                    checked={allSelected}
                    ref={(el) => { if (el) el.indeterminate = someSelected; }}
                    onChange={toggleAll}
                    className="h-4 w-4 cursor-pointer accent-primary"
                  />
                </th>
              )}
              {columns.map((col) => (
                <th
                  key={col.key}
                  className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
                  style={col.width ? { width: col.width } : undefined}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => {
              const key = keyFn(row) || i;
              const isSelected = selectable && selected.has(keyFn(row));
              const canSelect = selectable && (isRowSelectable ? isRowSelectable(row) : true);
              return (
                <tr
                  key={key}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={cn(
                    'border-b border-border/50 transition-colors duration-100 last:border-0',
                    onRowClick && 'cursor-pointer hover:bg-accent/50',
                    isSelected && 'bg-primary/10 hover:bg-primary/15',
                  )}
                >
                  {selectable && (
                    <td className="w-10 px-4 py-3.5" onClick={(e) => e.stopPropagation()}>
                      {canSelect && (
                        <input
                          type="checkbox"
                          aria-label="Select row"
                          checked={isSelected}
                          onChange={() => toggleOne(row)}
                          className="h-4 w-4 cursor-pointer accent-primary"
                        />
                      )}
                    </td>
                  )}
                  {columns.map((col) => (
                    <td key={col.key} className="px-4 py-3.5 text-foreground">
                      {col.render ? col.render(row) : row[col.key] ?? '\u2014'}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
